import "./CompanyUsers.css";
import DataTable from "./components/DataTable.jsx";
import { createPortal } from "react-dom";

import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

function CompanyUsers() {
    const [users, setUsers] = useState([]);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        role: "employee",
    });

    const [showEditModal, setShowEditModal] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);

    const [editingUser, setEditingUser] = useState({
        id: null,
        name: "",
        email: "",
        role: "employee",
        password: "",
    });

    const [permissionUser, setPermissionUser] = useState({
        id: null,
        name: "",
        role: "employee",
    });

    const [permissionItems, setPermissionItems] = useState([]);
    const [loadingPermissions, setLoadingPermissions] = useState(false);
    const [savingPermissions, setSavingPermissions] = useState(false);

    const getHeaders = () => {
        const token = localStorage.getItem("company_token");

        return {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
    };

    const fetchUsers = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_URL}/users/me/company-users`,
                getHeaders()
            );

            setUsers(response.data);
        } catch (error) {
            toast.error(
                error.response?.data?.detail || "Error cargando usuarios"
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        try {
            const storedCompany = localStorage.getItem("company_info");

            if (storedCompany) {
                setCompanyInfo(JSON.parse(storedCompany));
            }
        } catch {
            toast.error("No fue posible leer la información de la empresa.");
        }

        fetchUsers();
    }, [fetchUsers]);

    const handleChange = (event) => {
        const { name, value } = event.target;

        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            await axios.post(
                `${API_URL}/users/me/company-users`,
                formData,
                getHeaders()
            );

            toast.success("Usuario creado correctamente.");

            setFormData({
                name: "",
                email: "",
                password: "",
                role: "employee",
            });

            await fetchUsers();
        } catch (error) {
            toast.error(
                error.response?.data?.detail || "Error al crear el usuario"
            );
        }
    };

    const openEditModal = useCallback((user) => {
        setEditingUser({
            id: user.id,
            name: user.name || "",
            email: user.email || "",
            role: user.role || "employee",
            password: "",
        });

        setShowEditModal(true);
    }, []);

    const closeEditModal = () => {
        setShowEditModal(false);
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;

        setEditingUser((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const saveUser = async () => {
        try {
            const userData = {
                name: editingUser.name,
                email: editingUser.email,
                role: editingUser.role,
            };

            if (editingUser.password.trim()) {
                userData.password = editingUser.password;
            }

            await axios.put(
                `${API_URL}/users/${editingUser.id}`,
                userData,
                getHeaders()
            );

            toast.success("Usuario actualizado correctamente.");
            setShowEditModal(false);

            await fetchUsers();
        } catch (error) {
            toast.error(
                error.response?.data?.detail || "Error actualizando usuario"
            );
        }
    };
    const openPermissionsModal = async (user) => {
        setPermissionUser({
            id: user.id,
            name: user.name || "",
            role: user.role || "employee",
        });

        setPermissionItems([]);
        setShowPermissionsModal(true);
        setLoadingPermissions(true);

        try {
            const response = await axios.get(
                `${API_URL}/permissions/users/${user.id}`,
                getHeaders()
            );

            setPermissionItems(response.data);
        } catch (error) {
            toast.error(
                error.response?.data?.detail ||
                "No fue posible cargar los permisos del usuario."
            );

            setShowPermissionsModal(false);
        } finally {
            setLoadingPermissions(false);
        }
    };

    const togglePermission = (permissionId) => {
        setPermissionItems((current) =>
            current.map((permission) => {
                if (permission.permission_id !== permissionId) {
                    return permission;
                }

                return {
                    ...permission,
                    is_allowed: !permission.is_allowed,
                    has_override: true,
                };
            })
        );
    };

    const resetPermissionToRole = (permissionId) => {
        setPermissionItems((current) =>
            current.map((permission) => {
                if (permission.permission_id !== permissionId) {
                    return permission;
                }

                return {
                    ...permission,
                    is_allowed: permission.inherited_from_role,
                    has_override: false,
                };
            })
        );
    };

    const savePermissions = async () => {
        if (!permissionUser.id) {
            return;
        }

        setSavingPermissions(true);

        try {
            const user = users.find(
                (item) => item.id === permissionUser.id
            );

            if (!user) {
                toast.error("No fue posible encontrar el usuario.");
                return;
            }

            /* Primero actualiza el rol general del usuario. */
            await axios.put(
                `${API_URL}/users/${permissionUser.id}`,
                {
                    name: user.name,
                    email: user.email,
                    role: permissionUser.role,
                },
                getHeaders()
            );

            /*
              Solo se guardan los permisos modificados individualmente.
              Los demás se siguen heredando del rol asignado.
            */
            const overrides = permissionItems
                .filter((permission) => permission.has_override)
                .map((permission) => ({
                    permission_id: permission.permission_id,
                    is_allowed: permission.is_allowed,
                }));

            await axios.put(
                `${API_URL}/permissions/users/${permissionUser.id}`,
                {
                    permissions: overrides,
                },
                getHeaders()
            );

            toast.success("Rol y permisos actualizados correctamente.");

            setShowPermissionsModal(false);
            setPermissionItems([]);

            await fetchUsers();
        } catch (error) {
            toast.error(
                error.response?.data?.detail ||
                "No fue posible actualizar los permisos."
            );
        } finally {
            setSavingPermissions(false);
        }
    };

    const handleToggleActive = useCallback(
        async (user) => {
            const newStatus = !user.is_active;
            const action = newStatus ? "activar" : "inactivar";

            if (!window.confirm(`¿Deseas ${action} a ${user.name}?`)) {
                return;
            }

            try {
                await axios.put(
                    `${API_URL}/users/${user.id}`,
                    {
                        name: user.name,
                        email: user.email,
                        role: user.role,
                        is_active: newStatus,
                    },
                    getHeaders()
                );

                toast.success(
                    `Usuario ${newStatus ? "activado" : "inactivado"} correctamente.`
                );

                await fetchUsers();
            } catch (error) {
                toast.error(
                    error.response?.data?.detail ||
                    `No fue posible ${action} al usuario`
                );
            }
        },
        [fetchUsers]
    );

    const columns = useMemo(
        () => [
            {
                title: "Nombre",
                data: "name",
            },
            {
                title: "Correo",
                data: "email",
            },
            {
                title: "Rol",
                data: "role",
                render: (role) =>
                    role === "admin"
                        ? '<span class="role-badge admin">Administrador</span>'
                        : '<span class="role-badge employee">Empleado</span>',
            },
            {
                title: "Estado",
                data: "is_active",
                render: (isActive) =>
                    isActive
                        ? '<span class="status active">Activo</span>'
                        : '<span class="status inactive">Inactivo</span>',
            },
        ],
        []
    );

    const used = users.length;
    const max = companyInfo?.max_users || 0;
    const available = Math.max(max - used, 0);
    const limitReached = max > 0 && used >= max;
    const isAdmin = companyInfo?.role === "admin";

    if (loading) {
        return <h2>Cargando...</h2>;
    }

    return (
        <div className="company-users">
            <h1>Gestión de Usuarios</h1>

            <div className="stats-card">
                <h3>Empresa: {companyInfo?.company_name || "Sin información"}</h3>

                <p>
                    Usuarios usados: <strong>{used}</strong> / <strong>{max}</strong>
                </p>

                <p>
                    Disponibles: <strong>{available}</strong>
                </p>
            </div>

            {isAdmin && (
                <form className="user-form" onSubmit={handleSubmit}>
                    <h2>Crear Usuario</h2>

                    <input
                        type="text"
                        name="name"
                        placeholder="Nombre"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />

                    <input
                        type="email"
                        name="email"
                        placeholder="Correo"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />

                    <input
                        type="password"
                        name="password"
                        placeholder="Contraseña"
                        value={formData.password}
                        onChange={handleChange}
                        required
                    />

                    <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                    >
                        <option value="employee">Empleado</option>
                        <option value="admin">Administrador</option>
                    </select>

                    <button type="submit" disabled={limitReached}>
                        {limitReached ? "Límite alcanzado" : "Crear Usuario"}
                    </button>
                </form>
            )}

            <div className="table-container">
                <DataTable
                    data={users}
                    columns={columns}
                    onEdit={isAdmin ? openEditModal : undefined}
                    onPermissions={isAdmin ? openPermissionsModal : undefined}
                    onToggleActive={isAdmin ? handleToggleActive : undefined}
                />
            </div>

            {showEditModal &&
                createPortal(
                    <div
                        className="company-users-modal-overlay"
                        onMouseDown={closeEditModal}
                    >
                        <section
                            className="company-users-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="edit-user-title"
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <header className="company-users-modal-header">
                                <h2 id="edit-user-title">Editar usuario</h2>

                                <button
                                    type="button"
                                    className="company-users-modal-close"
                                    aria-label="Cerrar"
                                    onClick={closeEditModal}
                                >
                                    ×
                                </button>
                            </header>

                            <div className="company-users-modal-body">
                                <label htmlFor="edit-user-name">Nombre</label>
                                <input
                                    id="edit-user-name"
                                    type="text"
                                    name="name"
                                    value={editingUser.name}
                                    onChange={handleEditChange}
                                    required
                                />

                                <label htmlFor="edit-user-email">Correo</label>
                                <input
                                    id="edit-user-email"
                                    type="email"
                                    name="email"
                                    value={editingUser.email}
                                    onChange={handleEditChange}
                                    required
                                />

                                <label htmlFor="edit-user-password">
                                    Nueva contraseña
                                </label>
                                <input
                                    id="edit-user-password"
                                    type="password"
                                    name="password"
                                    placeholder="Déjalo vacío para conservar la actual"
                                    value={editingUser.password}
                                    onChange={handleEditChange}
                                />

                                <label htmlFor="edit-user-role">Rol</label>
                                <select
                                    id="edit-user-role"
                                    name="role"
                                    value={editingUser.role}
                                    onChange={handleEditChange}
                                >
                                    <option value="employee">Empleado</option>
                                    <option value="admin">Administrador</option>
                                </select>
                            </div>

                            <footer className="company-users-modal-footer">
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={closeEditModal}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="button"
                                    className="btn-save"
                                    onClick={saveUser}
                                >
                                    Guardar cambios
                                </button>
                            </footer>
                        </section>
                    </div>,
                    document.body
                )}

            {showPermissionsModal &&
                createPortal(
                    <div
                        className="company-users-modal-overlay"
                        onMouseDown={() => setShowPermissionsModal(false)}
                    >
                        <section
                            className="company-users-modal company-permissions-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="permissions-user-title"
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <header className="company-users-modal-header">
                                <div>
                                    <h2 id="permissions-user-title">
                                        Rol y permisos
                                    </h2>

                                    <p className="company-permissions-user-name">
                                        {permissionUser.name}
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    className="company-users-modal-close"
                                    aria-label="Cerrar"
                                    onClick={() => setShowPermissionsModal(false)}
                                >
                                    ×
                                </button>
                            </header>

                            <div className="company-users-modal-body">
                                <label htmlFor="permission-user-role">
                                    Rol asignado
                                </label>

                                <select
                                    id="permission-user-role"
                                    value={permissionUser.role}
                                    disabled={loadingPermissions || savingPermissions}
                                    onChange={(event) =>
                                        setPermissionUser((previous) => ({
                                            ...previous,
                                            role: event.target.value,
                                        }))
                                    }
                                >
                                    <option value="employee">Empleado</option>
                                    <option value="admin">Administrador</option>
                                </select>

                                <div className="company-permissions-help">
                                    <strong>Cómo funciona:</strong> el usuario hereda los
                                    permisos de su rol. Puedes activar o revocar permisos
                                    individuales sin modificar a los demás usuarios.
                                </div>

                                {loadingPermissions ? (
                                    <p className="company-permissions-loading">
                                        Cargando permisos...
                                    </p>
                                ) : (
                                    <div className="company-permissions-list">
                                        {permissionItems.map((permission) => (
                                            <article
                                                className="company-permission-item"
                                                key={permission.permission_id}
                                            >
                                                <div className="company-permission-content">
                                                    <strong>{permission.name}</strong>

                                                    <span>{permission.description}</span>

                                                    <small>
                                                        Código: {permission.code}
                                                        {" · "}
                                                        {permission.inherited_from_role
                                                            ? "Incluido en el rol"
                                                            : "No incluido en el rol"}
                                                    </small>
                                                </div>

                                                <div className="company-permission-actions">
                                                    <label className="company-permission-switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={permission.is_allowed}
                                                            disabled={savingPermissions}
                                                            onChange={() =>
                                                                togglePermission(
                                                                    permission.permission_id
                                                                )
                                                            }
                                                        />

                                                        <span className="company-permission-slider" />
                                                    </label>

                                                    {permission.has_override && (
                                                        <button
                                                            type="button"
                                                            className="company-permission-reset"
                                                            disabled={savingPermissions}
                                                            onClick={() =>
                                                                resetPermissionToRole(
                                                                    permission.permission_id
                                                                )
                                                            }
                                                        >
                                                            Restablecer
                                                        </button>
                                                    )}
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <footer className="company-users-modal-footer">
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    disabled={savingPermissions}
                                    onClick={() => setShowPermissionsModal(false)}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="button"
                                    className="btn-save"
                                    disabled={loadingPermissions || savingPermissions}
                                    onClick={savePermissions}
                                >
                                    {savingPermissions
                                        ? "Guardando..."
                                        : "Guardar permisos"}
                                </button>
                            </footer>
                        </section>
                    </div>,
                    document.body
                )}
        </div>
    );
}

export default CompanyUsers;